import { useCallback, useMemo } from 'react';

import { RequestsChartProps } from '../types';
import { formatXAxis } from '../utils/formatXAxis';
import { useContent } from './useContent';
import { IChartProps } from '../../Chart';
import { TranslationRequestWidget } from '../../../types';

interface UseRequestsChartProps
  extends Omit<
    RequestsChartProps,
    'className' | 'title' | 'NoDataPlaceholder'
  > {
  translation: TranslationRequestWidget;
}

const Y_AXIS_MAX_VALUE_COEFFICIENT = 1.2;

export const useRequestsChart = ({
  translation,
  data,
  isLoading,
  timeframe,
  hasFixedHeight = false,
}: UseRequestsChartProps) => {
  const xAxisTickFormatter = useCallback(
    (value: Date) =>
      formatXAxis(
        { time: translation.time(value), date: translation.date(value) },
        timeframe,
      ),
    [timeframe, translation],
  );

  const yAxisTickFormatter = useCallback(
    (value: number) => translation.callsCount(value),
    [translation],
  );

  const yAxisDomain = useMemo(() => {
    const maxValue = Math.max(...data.map(item => Number(item.value)));

    return [0, maxValue * Y_AXIS_MAX_VALUE_COEFFICIENT];
  }, [data]);

  const chartProps: IChartProps = useMemo(
    () => ({
      type: 'linear',
      translation,
      data,
      hasFixedHeight,
      loading: isLoading,
      xAxisTickFormatter,
      yAxisTickFormatter,
      yAxisDomain,
    }),
    [
      data,
      isLoading,
      translation,
      hasFixedHeight,
      xAxisTickFormatter,
      yAxisTickFormatter,
      yAxisDomain,
    ],
  );

  const { hasPreloader, hasChart, hasPlaceholder } = useContent({
    data,
    isLoading,
  });

  return { chartProps, hasChart, hasPlaceholder, hasPreloader };
};
